import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { publicApiClient } from '@/api/public'
import { isColourDark } from '@/lib/brandTheme'
import { formatPriceLabel } from '@/lib/pricing'
import { CalendarDays, Clock, MapPin, Users, ChevronRight, Globe } from 'lucide-react'
import { moneyFor } from '@/lib/money'
import { datesFor } from '@/lib/dates'

export default function PublicSessionsPage() {
  const { slug } = useParams<{ slug: string }>()

  const { data, isLoading, isError } = useQuery({
    queryKey: ['public-sessions', slug],
    queryFn: () => publicApiClient.getPublicSessions(slug!).then(r => r.data),
    enabled: !!slug,
  })
  const { fmt: fmtMoney } = moneyFor(data?.currency)
  const { formatDate, formatTime } = datesFor(data?.region, data?.timeZoneId)
  const fmtDate = (iso: string) => {
    return formatDate(iso, { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' })
  }
  const fmtTime = (iso: string) => {
    return formatTime(iso)
  }

  if (isLoading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
    </div>
  )

  if (isError || !data) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <p className="text-lg font-semibold text-slate-700">Training provider not found</p>
        <p className="text-sm text-slate-400 mt-1">Check the link and try again.</p>
      </div>
    </div>
  )

  const hasPrimary = !!data.primaryColour
  const darkBg = hasPrimary ? isColourDark(data.primaryColour!) : false
  const headerStyle = hasPrimary ? { backgroundColor: data.primaryColour! } : undefined
  const headerClass = hasPrimary ? '' : 'bg-white border-b border-slate-200'
  const textClass = hasPrimary ? (darkBg ? 'text-white' : 'text-slate-900') : 'text-slate-900'
  const subTextClass = hasPrimary ? (darkBg ? 'text-white/70' : 'text-slate-700') : 'text-slate-500'
  const linkClass = hasPrimary ? (darkBg ? 'text-white/80 hover:text-white' : 'text-slate-700 hover:text-slate-900') : 'text-[var(--brand-accent)] hover:opacity-80'

  const accent = data.secondaryColour || data.primaryColour || '#4F46E5'

  return (
    <div className="min-h-screen bg-slate-50" style={{ '--brand-accent': accent } as React.CSSProperties}>
      {/* Header */}
      <div className={headerClass} style={headerStyle}>
        <div className="max-w-3xl mx-auto px-4 py-6 flex items-center gap-4">
          {data.tenantLogoUrl && (
            <img src={data.tenantLogoUrl} alt={data.tenantName} className="h-10 w-auto object-contain" />
          )}
          <div className="flex-1">
            <h1 className={`text-xl font-bold ${textClass}`}>{data.tenantName}</h1>
            <p className={`text-sm ${subTextClass}`}>Available training sessions</p>
          </div>
          <Link to={`/attendee/${slug}/login`} className={`text-sm font-medium whitespace-nowrap ${linkClass}`}>
            My certificates →
          </Link>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        {data.sessions.length === 0 ? (
          <div className="text-center py-16">
            <CalendarDays size={40} className="mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">No upcoming sessions available at this time.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {data.sessions.map(session => (
              <Link
                key={session.id}
                to={`/sessions/${slug}/${session.id}`}
                className="block bg-white border border-slate-200 rounded-xl p-5 hover:border-[var(--brand-accent)] hover:shadow-sm transition-all group"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0 space-y-2">
                    <h2 className="font-semibold text-slate-900 group-hover:text-[var(--brand-accent)] transition-colors">
                      {session.courseName}
                    </h2>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-500">
                      <span className="flex items-center gap-1.5">
                        <CalendarDays size={14} className="text-slate-400" />
                        {fmtDate(session.startsAt)}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <Clock size={14} className="text-slate-400" />
                        {fmtTime(session.startsAt)}
                        {session.endsAt && ` – ${fmtTime(session.endsAt)}`}
                      </span>
                      {session.deliveryMethod === 'Online' ? (
                        <span className="flex items-center gap-1.5">
                          <Globe size={14} className="text-slate-400" />
                          Online
                        </span>
                      ) : session.venue && (
                        <span className="flex items-center gap-1.5">
                          <MapPin size={14} className="text-slate-400" />
                          {session.venue.name}{session.venue.city ? `, ${session.venue.city}` : ''}
                        </span>
                      )}
                      <span className="flex items-center gap-1.5">
                        <Users size={14} className="text-slate-400" />
                        {session.availablePlaces} place{session.availablePlaces !== 1 ? 's' : ''} remaining
                      </span>
                    </div>
                  </div>
                  <div className="shrink-0 text-right flex flex-col items-end gap-2">
                    {(() => {
                      const label = formatPriceLabel(session.pricingModel, session.price, session.flatFeeAmount, session.flatFeeCapacity, fmtMoney)
                      return label ? (
                        <span className="text-lg font-bold text-slate-900">{label}</span>
                      ) : (
                        <span className="text-sm text-slate-400">Free</span>
                      )
                    })()}
                    <span className="text-[var(--brand-accent)] group-hover:translate-x-0.5 transition-transform">
                      <ChevronRight size={18} />
                    </span>
                  </div>
                </div>
                {session.availablePlaces === 0 && (
                  <div className="mt-3 text-xs font-semibold text-amber-600 bg-amber-50 rounded px-2 py-1 w-fit">
                    Fully booked
                  </div>
                )}
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
